import {
  Button,
  Upload,
  UploadProps,
  Image,
  Radio,
  RadioChangeEvent,
  message,
} from "antd";
import CustomCard from "../../shared/CustomCard/CustomCard";
import { UploadOutlined } from "@ant-design/icons";
import { getImage } from "../../../services/image-utils";
import { useContext, useState } from "react";
import {
  getProfilePicture,
  updateLoginInformation,
  updatePassword,
  updateProfilePicture,
} from "../../../services/users.service";
import { UserContext } from "../../../context/user.context";
import { CBInput } from "../../shared";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { loginInformationSchema, loginInformationValues } from "../utils";
import { UsersTokensDTO } from "../../../interfaces/users-tokens.interface";
import User from "../../../interfaces/user.interface";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type TabPosition = "information" | "password";
const LoginInformation = () => {
  const [messageApi, contextHolderMessage] = message.useMessage();
  const { user, updateUser } = useContext(UserContext);
  const { control, watch } = useForm({
    mode: "onBlur",
    resolver: yupResolver(loginInformationSchema),
    values: loginInformationValues(user!),
  });

  const [mode, setMode] = useState<TabPosition>("information");
  const [preview, setPreview] = useState("");
  const handleModeChange = (e: RadioChangeEvent) => {
    setMode(e.target.value);
  };
  const [fileList, setFileList] = useState<any>([]);
  const queryClient = useQueryClient();
  const { data: url } = useQuery({
    queryKey: [`profile-picture`, user?.id],
    queryFn: async () => {
      if (user?.profilePicture || user?.id) {
        try {
          const result = await getProfilePicture(user?.id!);
          return result.url;
        } catch (err) {
          return getImage("/assets/profile.png");
        }
      }
    },
    staleTime: Infinity,
    enabled: !!user?.id,
  });
  const uploadProps: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    onChange: (info) => {
      const file = info.fileList[0];
      if (file?.originFileObj)
        setPreview(URL.createObjectURL(file.originFileObj!));
    },
    fileList,
  };
  const changeImageMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append("file", fileList[0]);
      await updateProfilePicture(user?.id!, formData);
    },
    onSuccess: () => {
      notificationMessage(true, "Upload image process ends successfully");
      if (user) updateUser({ ...user, updated: true });
      setPreview("");
      queryClient.invalidateQueries({
        queryKey: [`profile-picture`, user?.id],
      });
    },
    onError: () => {
      notificationMessage(false, "Error: Upload image was not success!");
    },
  });

  const handleUpdatePwd = async () => {
    try {
      await updatePassword({
        userId: user?.id!,
        password: watch("newPwd"),
        passwordConfirmation: watch("confirmPwd"),
        currentPassword: watch("currentPwd"),
      } as UsersTokensDTO);
      notificationMessage(true, "The password was changes successfully");
    } catch (error) {
      notificationMessage(
        false,
        "Error: Change password process was not success!",
      );
    }
  };

  const handleUpdateInfo = async () => {
    try {
      await updateLoginInformation({
        id: user?.id!,
        email: watch("email"),
        firstName: watch("firstName"),
        lastName: watch("lastName"),
      });
      const props: User = {
        ...user!,
        firstName: watch("firstName") ?? "",
        lastName: watch("lastName") ?? "",
        email: watch("email"),
      };
      updateUser(props);
      notificationMessage(
        true,
        "The login information was changes successfully",
      );
    } catch (error) {
      notificationMessage(
        false,
        "Error: Change login information process was not success!",
      );
    }
  };

  const notificationMessage = (type: boolean, body: string) => {
    messageApi.open({
      type: type ? "success" : "error",
      content: body,
      duration: 4,
      style: { marginTop: "6vh" },
    });
  };

  return (
    <div className="flex justify-center gap-8 pt-8">
      <CustomCard title="Profile picture">
        <div className="flex flex-col gap-4">
          <Upload maxCount={1} {...uploadProps}>
            <Button icon={<UploadOutlined />}>Choose file</Button>
          </Upload>
          <div
            className="h-80 w-80"
            style={{
              height: "18.5rem",
              overflow: "hidden",
            }}
          >
            <Image width={300} preview={false} src={preview || url} />
          </div>
          <div className="flex">
            <Button
              type="primary"
              htmlType="submit"
              style={{ marginTop: "1rem", flex: "1" }}
              icon={<UploadOutlined />}
              onClick={() => changeImageMutation.mutate()}
            >
              Save upload
            </Button>
          </div>
        </div>
      </CustomCard>
      <CustomCard title="Login credentials">
        <Radio.Group
          size="large"
          optionType="button"
          buttonStyle="solid"
          onChange={handleModeChange}
          value={mode}
          style={{ marginBottom: 8 }}
        >
          <Radio.Button value="information">Information</Radio.Button>
          <Radio.Button value="password">Password</Radio.Button>
        </Radio.Group>
        <div>
          {mode === "information" && (
            <div className="flex flex-col">
              <div className="pt-4">
                <CBInput
                  name="email"
                  control={control}
                  label="Email Address"
                  placeholder="(Also your login ID)"
                />
              </div>
              <div className="flex gap-4">
                <div>
                  <CBInput
                    name="firstName"
                    control={control}
                    label="First Name"
                  />
                </div>
                <div>
                  <CBInput
                    name="lastName"
                    control={control}
                    label="Last Name"
                  />
                </div>
              </div>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{ marginTop: "9.5rem", flex: "1" }}
                onClick={() => handleUpdateInfo()}
              >
                Update Information
              </Button>
            </div>
          )}
          {mode === "password" && (
            <div className="flex flex-col">
              <div className="pt-4">
                <CBInput
                  name="currentPwd"
                  control={control}
                  label="Current Password"
                  type="password"
                  placeholder="Do you remember your password?"
                />
              </div>
              <div className="flex gap-4">
                <div>
                  <CBInput
                    name="newPwd"
                    control={control}
                    type="password"
                    label="New Password"
                  />
                </div>
                <div>
                  <CBInput
                    name="confirmPwd"
                    control={control}
                    type="password"
                    label="Confirm Password"
                  />
                </div>
              </div>
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                style={{ marginTop: "9.5rem", flex: "1" }}
                onClick={() => handleUpdatePwd()}
              >
                Update Password
              </Button>
            </div>
          )}
        </div>
      </CustomCard>
      {contextHolderMessage}
    </div>
  );
};

export default LoginInformation;
